import { useAuthStore } from '@/stores/authStore'
import type { PlanStatusLimits } from '@/lib/api'

/**
 * Client-side read of the account's free-trial state, fed by
 * `authStore.planStatus` (fetched once at login/boot from
 * `GET /auth/plan-status` -- every role, unlike `billingStore.subscription`
 * which 403s for Manager/Rep/Analyst). Powers the trial-aware UI on
 * Contacts/Workflows/Campaigns/Materials (cap banners, friendly blocked
 * messages) and the Billing page's Free Trial card.
 *
 * @example
 *   const { isTrial, limits } = useTrialStatus()
 *   if (isTrial && limits) return <p>{count} of {limits.workflows} workflows used</p>
 */
export interface TrialStatus {
  /** True only when the account's active plan is the free trial. */
  isTrial: boolean
  /** The trial's numeric caps, or null for a paid/no-active-plan account. */
  limits: PlanStatusLimits | null
}

export function useTrialStatus(): TrialStatus {
  const planStatus = useAuthStore((s) => s.planStatus)
  return {
    isTrial: planStatus?.is_trial ?? false,
    limits: planStatus?.limits ?? null,
  }
}
